<template>
	<div class="row">
		<div class="col-12 mb-3 d-flex flex-row align-items-center justify-content-between">
			<h1>{{ activity.id ? $t('update-reflection') : $t('new-reflection') }}</h1>

            <button v-if="activity.id" class="btn btn-primary" @click="saveActivity">
                {{ $t('save-changes') }}
            </button>
		</div>

        <div class="col-12 col-xl-7 position-relative">

            <!-- Reflection -->
			<textarea-autosize
                v-model="activity.reflection"
                class="form-control mb-1"
                placeholder="What are you thinking about?"
                :label="$t('reflection')"
                :srOnly="true"
            />

            <div class="d-flex flex-row align-items-center flex-wrap mb-3">
                <b-icon icon="file-earmark-arrow-up" @click="openFileUploadModal" class="ml-1 mr-3 my-3" scale="1.5" style="cursor: pointer;" />
                <b-icon ref="emojiToggle" icon="emoji-smile" class="mx-3 my-3" scale="1.5" @click="toggleShowEmojis($event)" style="cursor: pointer;"/>

                <div class="ml-auto">
                    <button
                        v-if="!activity.id"
                        type="submit" class="btn btn-primary mt-1" @click="openSaveDialog" :disabled="this.activity.reflection.length === 0">
                        {{ $t('submit') }}
                    </button>
                </div>
            </div>

            <!-- Emoji Text Insert -->
            <div
                v-if="showEmojis"
                class="position-absolute top-0 left-0"
                :style="dialogStyle"
                ref="emojiDialog"
            >
                <VEmojiPicker
                    :style="{ width: '324', height: '200' }"
                    :labelSearch="$t('search-ellipses')"
                    @select="onSelectEmoji"
                />
            </div>

            <!-- Assets -->
            <div v-if="activity.assets.length" class="list-group border-radius-2 no-border mb-3">
                <div class="list-group-item text-sm font-weight-bold">{{ $t('files') }}</div>
                <div class="list-group-item d-flex flex-row align-items-center flex-wrap">
                    <AssetBadge
                        v-for="asset in activity.assets"
                        :key="asset.id"
                        :file="asset"
                        class="mr-2"
                        :showRemove="true"
                        @remove="removeAsset(asset)"
                    />
                </div>
            </div>

            <!-- Treatment Sessions -->
            <div v-if="activity.treatmentsessions.length" class="list-group border-radius-2 no-border mb-3">
                <div class="list-group-item text-sm font-weight-bold">
                    {{ $t('treatment-session') }}
                </div>
                <TreatmentSessionListItem
                    v-for="session in activity.treatmentsessions"
                    :key="session.id"
                    :session="session"
                    :showRemove="true"
                    @remove="activity.treatmentsessions = activity.treatmentsessions.filter(el => el.id !== session.id)"
                />
            </div>

            <!-- Competency Test Sessions -->
            <div v-if="activity.competencytestsessions.length" class="list-group border-radius-2 no-border mb-3">
                <div class="list-group-item text-sm font-weight-bold">
                    {{ $t('tests') }}
                </div>
                <TestSessionListItem
                    v-for="session in activity.competencytestsessions"
                    :key="session.id"
                    :session="session"
                    class="list-group-item"
                    :showRemove="true"
                    @remove="activity.competencytestsessions = activity.competencytestsessions.filter(el => el.id !== session.id)"
                />
            </div>

            <!-- Form Responses -->
            <div v-if="activity.formresponses.length" class="list-group border-radius-2 no-border mb-3">
                <div class="list-group-item text-sm font-weight-bold">
                    {{ $t('forms') }}
                </div>
                <FormResponseListItem
                    v-for="response in activity.formresponses"
                    :key="response.formresponse_id"
                    :formResponse="response"
                    class="list-group-item"
                    :showRemove="true"
                    @remove="activity.formresponses = activity.formresponses.filter(el => el.formresponse_id !== response.formresponse_id)"
                />
            </div>

            <!-- Learning Outcomes -->
            <div v-if="activity.learningoutcomes.length" class="list-group border-radius-2 no-border mb-3">
                <div class="list-group-item text-sm font-weight-bold">
                    {{  $t('learning-outcomes') }}
                </div>
                <div v-for="outcome in activity.learningoutcomes" :key="outcome.id" class="list-group-item d-flex flex-row align-items-center justify-content-between">
                    <div>{{ outcome.title }}</div>
                    <b-icon icon="x" @click.stop="activity.learningoutcomes = activity.learningoutcomes.filter(el => el.id !== outcome.id)" style="cursor: pointer;"/>
                </div>
            </div>

            <!-- Tag Selector -->
            <TagGroups v-model="activity.tags" class="mb-5" />
        </div>

        <div class="col-12 col-xl-5">

            <!-- Attach Reflections to ... -->
            <ul class="nav nav-pills mb-3">
                <li class="nav-item">
                    <a class="nav-link" @click="activeTab = 'TREATMENTS'" :class="{ 'active': activeTab == 'TREATMENTS' }">
                        {{ $t('treatment-sessions') }}
                    </a>
                </li>
                <li class="nav-item">
                    <a class="nav-link" @click="activeTab = 'TESTS'" :class="{ 'active': activeTab == 'TESTS' }">
                        {{ $t('tests') }}
                    </a>
                </li>
                <li class="nav-item">
                    <a class="nav-link" @click="activeTab = 'OUTCOMES'" :class="{ 'active': activeTab == 'OUTCOMES' }">
                        {{ $t('learning-outcomes') }}
                    </a>
                </li>
                <li class="nav-item">
                    <a class="nav-link" @click="activeTab = 'FORMS'" :class="{ 'active': activeTab == 'FORMS' }">
                        {{ $t('forms') }}
                    </a>
                </li>
            </ul>

            <!-- Treatment Session Selector -->
            <div v-if="activeTab === 'TREATMENTS'" id="treatmentSessionScroll" style="height: 80vh; overflow: hidden; overflow-y: auto" class="list-group border-radius-2 no-border">
                <TreatmentSessionSelector v-model="activity.treatmentsessions" scrollElementId="treatmentSessionScroll" />
            </div>

            <!-- Competency Test Session Selector -->
            <div v-if="activeTab === 'TESTS'" id="testSessionScroll" style="height: 80vh; overflow: hidden; overflow-y: auto" class="list-group border-radius-2 no-border">
                <TestSessionSelector v-model="activity.competencytestsessions" scrollElementId="testSessionScroll" />
            </div>

            <!-- Form Resposne Selector -->
            <div v-if="activeTab === 'FORMS'" id="formResponseScroll" style="height: 80vh; overflow: hidden; overflow-y: auto" class="list-group border-radius-2 no-border">
                <FormResponseSelector v-model="activity.formresponses" scrollElementId="formResponseScroll" />
            </div>

            <!-- Learning Outcome Selector -->
            <div v-if="activeTab === 'OUTCOMES'" id="outcomeScroll" style="height: 80vh; overflow: hidden, overflow-y: auto" class="list-group border-radius-2 no-border">
                <LearningOutcomeSelector v-model="activity.learningoutcomes" scrollElementId="outcomeScroll" />
            </div>
        </div>
    </div>
</template>

<script>
import { VEmojiPicker } from "v-emoji-picker";
import TagGroups from "../../components/Tags/GroupedTagSelector.vue";
import TreatmentSessionSelector from "../../components/TreatmentSessions/SessionSelector.vue";
import TreatmentSessionListItem from "../../components/TreatmentSessions/SessionListItem.vue";
import TestSessionSelector from "../../components/CompetencyTests/SessionSelector.vue";
import TestSessionListItem from "../../components/CompetencyTests/SessionListItem.vue";
import FormResponseSelector from "../../components/FormResponses/Selector.vue";
import FormResponseListItem from "../../components/FormResponses/ListItem.vue";
import LearningOutcomeSelector from "../../components/LearningOutcomes/ScrollingSelector.vue";
import AssetBadge from "../../components/Asset/AssetBadge.vue";

export default {
    components: {
        VEmojiPicker,
        TagGroups,
        TreatmentSessionSelector,
        TreatmentSessionListItem,
        TestSessionSelector,
        TestSessionListItem,
        FormResponseSelector,
        FormResponseListItem,
        LearningOutcomeSelector,
        AssetBadge,
    },
    mounted() {
        if (this.activity.id) this.fetchActivity().then(() => this.loading = false)
    },
    beforeDestroy() {
        const promises = []
        this.unattachedAssets.forEach(asset => {
            promises.push(this.deleteAsset(asset))
        })
        Promise.all(promises).catch(err => {
            console.error(err)
            this.$log.error.call(this, err)
        })
    },
    data() {
        return {
            loading: !!this.$route.params.activityId,
            showEmojis: false,
            dialogStyle: {
                top: '0',
                left: '0',
            },
            activity: {
                id: Number(this.$route.params.activityId),
                reflection: '',
                competencytestsessions: [],
                formresponses: [],
                learningoutcomes: [],
                treatmentsessions: [],
                tags: [],
                assets: [],
            },
            activeTab: 'TREATMENTS', // TESTS, FORMS, TREATMENTS, OUTCOMES
            unattachedAssets: [],
        }
    },
    methods: {
        openSaveDialog() {
            if (this.activity.tags.length > 0) {
                this.saveActivity()
                return
            }

            this.$store.commit("openDialog", {
                cancelBtn: true,
                title: this.$t("no-tags-selected"),
                content: this.$t("no-tags-selected-dialog"),
                confirmCallBack: this.saveActivity,
            })
        },
        fetchActivity() {
            if (!this.activity.id) return

            this.$store.commit('showBlockingSpinner')

            return this.$cfetch(`${this.$settings.APIBaseURL}/activitylog/get`, {
                method: 'GET',
                data: {
                    id: this.$route.params.activityId
                }
            }).then((res) => {
                if (res.status !== 'SUCCESS') throw new Error('Unexpected error fetching activity')

                this.activity = res.result
            }).catch((err) => {
                console.error(err)
                this.$log.error.call(this, err)

                this.$store.commit('openDialog', {
                    title: this.$t('something-went-wrong'),
                    content: this.$t('error-fetching-activity'),
                    confirmCallBack: function () { this.$router.push('/') }
                })
            }).finally(() => {
                this.$store.commit('hideBlockingSpinner')
            })
        },
        saveActivity() {
            this.$store.commit('showBlockingSpinner')

            this.$cfetch(`${this.$settings.APIBaseURL}/activitylog/save`, {
                method: 'POST',
                data: {
                    ...this.activity,
                    treatmentsessions: this.activity.treatmentsessions.map(el => el.id),
                    competencytestsessions: this.activity.competencytestsessions.map(el => el.CompetencyTestSessionID),
                    formresponses: this.activity.formresponses.map(el => el.formresponse_id),
                    learningoutcomes: this.activity.learningoutcomes.map(el => el.id),
                    assets: this.activity.assets.map(el => el.id),
                }
            }).then((res) => {
                if (res.status !== 'SUCCESS') {
                    this.$store.commit('openDialog', {
                        title: this.$t('something-went-wrong'),
                        content: this.$t('error-posting-reflection'),
                        confirmCallBack: function () {},
                    })
                } else {
                    this.unattachedAssets = [];
                    this.$router.push('/')
                }
            }).catch((err) => {
                this.$log.error.call(this, err)
            }).finally(() => {
                this.$store.commit('hideBlockingSpinner')
            })
        },
        onSelectEmoji(emoji) {
            this.activity.reflection += emoji.data;
        },
        toggleShowEmojis() {
            this.showEmojis = !this.showEmojis;

            if (this.showEmojis) {
                const buttonRect = this.$refs.emojiToggle.getBoundingClientRect();

                this.dialogStyle = {
                    top: buttonRect.top + 'px',
                    left: buttonRect.left + 'px',
                    zIndex: 100,
                }
            }
        },
        openFileUploadModal() {
            const self = this

            this.$store.commit("openModal", {
				type: "fileUpload",
				title: this.$t('upload-file(s)'),
				uploadCallback: function(files, result) {
                    self.activity.assets.push(...result)
                    self.unattachedAssets.push(...result)
				}
			});
        },
        removeAsset(asset) {
            this.activity.assets = this.activity.assets.filter(el => el.id !== asset.id)
            this.deleteAsset(asset)
        },
        deleteAsset(asset) {
            return this.$cfetch(`${this.$settings.APIBaseURL}/asset/delete`, {
                method: 'DELETE',
                data: { asset_id: asset.id },
            }).then(res => {
                if (res.status !== 'SUCCESS') throw new Error('Unexpected error deleting asset')
            })
        }
    },
    computed: {
        isTeacher: function() {
			return this.$UserSession.hasPriv.call(this, "APP_TEACHER");
		},
		isStudent: function() {
			return this.$UserSession.hasPriv.call(this, "APP_STUDENT");
		},
		canViewAllTests() {
			return this.$UserSession.hasPriv.call(this, "VIEW_ALLTESTS");
		}
    },
}
</script>
